import React from "react";
import {
  Box,
  Center,
  Divider,
  Flex,
  FormControl,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { getUnitsMeasurement } from "util/getUnitsByEnvironmentalVariableId/getUnitsByEnvironmentalVariableId";
import i18n from "../../../i18n";
import { Info } from "lucide-react";

interface ReadOnlyCondition {
  environmentalVariable: string;
  environmentalVariableId: number;
  environmentalVariableName?: string;
  comparisonOperator: string;
  value: number;
}

interface ReadOnlyConditionGroup {
  id: string;
  conditions: ReadOnlyCondition[];
  groupOperator: string;
  conditionOperator: string;
}

interface ReadOnlyConditionBuilderProps {
  conditionGroups: ReadOnlyConditionGroup[];
  environmentalVariables?: IEnvironmentalVariable[];
  t: (key: string) => string;
}

// Disabled select styles - consistent appearance
const disabledSelectStyles = {
  borderRadius: 8,
  backgroundColor: "#f0f1f2",
  fontSize: 14,
  size: "md" as const,
  color: "rgba(30, 82, 151, 0.8)",
  _focus: { borderColor: "none" },
  _disabled: {
    opacity: 1,
    cursor: "not-allowed",
    backgroundColor: "#f0f1f2",
    color: "rgba(30, 82, 151, 0.8)",
  },
};

// Read-only condition item
const ReadOnlyConditionItem: React.FC<{
  condition: ReadOnlyCondition;
  environmentalVariables?: IEnvironmentalVariable[];
  t: (key: string) => string;
}> = ({ condition, environmentalVariables, t }) => {
  const variableName =
    condition.environmentalVariableName ||
    environmentalVariables?.find(
      (v) => v.id === condition.environmentalVariableId
    )?.name ||
    (condition.environmentalVariable
      ? i18n.t(`environmentalVariableEnum.${condition.environmentalVariable}`)
      : "");

  const getOperatorText = (operator: string) => {
    switch (operator) {
      case ">":
        return t("isGreaterThan");
      case ">=":
        return t("isEqualToOrGreaterThan");
      case "=":
        return t("isEqualTo");
      case "<":
        return t("isLessThan");
      case "<=":
        return t("isEqualToOrLessThan");
      default:
        return operator;
    }
  };

  const unit = getUnitsMeasurement(
    condition.environmentalVariableId,
    i18n.language as "pt-BR" | "en"
  );

  return (
    <Grid templateColumns="repeat(12, 1fr)" gap={4}>
      <GridItem colSpan={4}>
        <FormControl>
          <Select
            {...disabledSelectStyles}
            value={condition.environmentalVariableId}
            isDisabled
          >
            <option value={condition.environmentalVariableId}>
              {variableName}
            </option>
          </Select>
        </FormControl>
      </GridItem>

      <GridItem colSpan={4}>
        <FormControl>
          <Select
            {...disabledSelectStyles}
            value={condition.comparisonOperator}
            isDisabled
          >
            <option value={condition.comparisonOperator}>
              {getOperatorText(condition.comparisonOperator)}
            </option>
          </Select>
        </FormControl>
      </GridItem>

      <GridItem colSpan={4}>
        <FormControl>
          <InputGroup size="md">
            <Input
              value={condition.value}
              isDisabled
              borderRadius={unit ? "8px 0 0 8px" : 8}
              backgroundColor="#f0f1f2"
              fontSize={14}
              color="rgba(30, 82, 151, 0.8)"
              _disabled={{
                opacity: 1,
                cursor: "not-allowed",
                backgroundColor: "#f0f1f2",
                color: "rgba(30, 82, 151, 0.8)",
              }}
              readOnly
            />
            {unit && (
              <InputRightAddon
                backgroundColor="#f0f1f2"
                borderColor="#E2E8F0"
                color="rgba(30, 82, 151, 0.8)"
                fontSize={14}
                px={2}
                minW="fit-content"
              >
                {unit}
              </InputRightAddon>
            )}
          </InputGroup>
        </FormControl>
      </GridItem>
    </Grid>
  );
};

// Read-only group component
const ReadOnlyGroup: React.FC<{
  group: ReadOnlyConditionGroup;
  groupIndex: number;
  environmentalVariables?: IEnvironmentalVariable[];
  t: (key: string) => string;
}> = ({ group, groupIndex, environmentalVariables, t }) => {
  return (
    <Box borderRadius={8}>
      <Stack spacing={4}>
        <Text size="md" fontWeight={"bold"} color="newBlue.500">
          {t("group")} {groupIndex + 1}
        </Text>

        <Flex gap={2} alignItems="center">
          <Text color="newBlue.500">{t("type")}</Text>
          <Select
            width="80px"
            borderRadius={8}
            backgroundColor="#ffffff"
            borderColor="newBlue.500"
            fontSize={14}
            size="sm"
            color="rgba(30, 82, 151, 0.8)"
            _focus={{ borderColor: "none" }}
            _disabled={{
              opacity: 1,
              cursor: "not-allowed",
              backgroundColor: "#ffffff",
              color: "rgba(30, 82, 151, 0.8)",
            }}
            value={group.conditionOperator}
            isDisabled
          >
            <option value={"and"}>{t("and")}</option>
            <option value={"or"}>{t("or")}</option>
          </Select>
          <Tooltip
            label={t("andVerifyConditionTrueOrVerifyAtLeastConditionTrue")}
            fontSize="md"
            hasArrow
          >
            <Info color={"#7e7b7b"} size="20px" />
          </Tooltip>
        </Flex>

        <Stack spacing={3}>
          {group.conditions.map((condition, conditionIndex) => (
            <Box key={`${condition.environmentalVariableId}-${conditionIndex}`}>
              {conditionIndex > 0 && (
                <Center py={2} mb={2}>
                  <Divider borderColor="newBlue.500" />
                  <Text
                    fontSize="md"
                    fontWeight={"bold"}
                    color="newBlue.500"
                    px={2}
                  >
                    {t(`${group.conditionOperator}`)}
                  </Text>
                  <Divider borderColor="newBlue.500" />
                </Center>
              )}
              <ReadOnlyConditionItem
                condition={condition}
                environmentalVariables={environmentalVariables}
                t={t}
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export const ReadOnlyConditionBuilder: React.FC<ReadOnlyConditionBuilderProps> =
  ({ conditionGroups, environmentalVariables, t }) => {
    if (!conditionGroups || conditionGroups.length === 0) {
      return null;
    }

    return (
      <Stack spacing={6}>
        <Stack spacing={4}>
          {conditionGroups.map((group, groupIndex) => (
            <Box
              key={group.id}
              p={4}
              mt={groupIndex > 0 ? 3 : 0}
              bg="#ffffff"
              borderRadius={8}
              alignItems="center"
              position="relative"
            >
              {groupIndex > 0 && (
                <Center mt={-10} mb={2}>
                  <Select
                    width="80px"
                    borderRadius={8}
                    borderColor="newBlue.500"
                    fontSize={14}
                    size="sm"
                    _focus={{ borderColor: "none" }}
                    value={conditionGroups[groupIndex - 1].groupOperator}
                    isDisabled
                    backgroundColor="#f0f1f2"
                    color="rgba(30, 82, 151, 0.6)"
                    cursor="not-allowed"
                    _disabled={{ opacity: 1, backgroundColor: "#f0f1f2" }}
                  >
                    <option value={"and"}>{t("and")}</option>
                    <option value={"or"}>{t("or")}</option>
                  </Select>
                </Center>
              )}

              <ReadOnlyGroup
                group={group}
                groupIndex={groupIndex}
                environmentalVariables={environmentalVariables}
                t={t}
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    );
  };
